import { Channel } from 'phoenix';
import WebSocket from '@/socket';
import { Store } from 'vuex';
import { PanelState } from '@/store/state';

function safeObject(value: unknown): object {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as object;
    }

    return {};
}

function safeNumber(value: unknown, fallback = 0): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    return fallback;
}

export default function createAgentChannel(
    name: string,
    socket: WebSocket,
    store: Store<PanelState>,
    onInit: () => void,
): Channel {
    const channel = socket.channel(`agent:${name}`);

    channel.on('agent_init', (data) => {
        store.dispatch('updateTiers', {
            tiers: data.tiers,
            name,
        });

        store.dispatch('updateQueueCalls', {
            queueCalls: safeObject(data.queue_calls),
            name,
        });

        store.dispatch('updateCallStates', {
            callStates: safeObject(data.call_states),
            name,
        });

        store.dispatch('updateLastActivityAtSecs', {
            lastActivityAtSecs: safeNumber(data.last_activity_at_secs, 0),
            name,
        });

        onInit();
    });

    channel.on('agent_state_change', (data) => {
        store.dispatch('changeAgentState', {
            agentState: data.agent_state,
            name,
        });
    });

    channel.on('tier_added', (data) => {
        store.dispatch('updateTiers', {
            tiers: data.tiers,
            name,
        });
    });

    channel.on('tier_removed', (data) => {
        store.dispatch('updateTiers', {
            tiers: data.tiers,
            name,
        });
    });

    channel.on('bridge_agent_start', (data) => {
        store.dispatch('updateQueueCalls', {
            queueCalls: safeObject(data.queue_calls),
            name,
        });
    });

    channel.on('bridge_agent_end', (data) => {
        store.dispatch('updateQueueCalls', {
            queueCalls: safeObject(data.queue_calls),
            name,
        });
    });

    channel.on('call_states', (data) => {
        store.dispatch('updateCallStates', {
            callStates: safeObject(data.call_states),
            name,
        });
    });

    channel.on('update_registration', (data) => {
        store.dispatch('updateAgentRegistration', {
            connected: data.registered,
            name,
        });
    });

    channel.on('refresh_last_activity', (data) => {
        store.dispatch('updateLastActivityAtSecs', {
            lastActivityAtSecs: safeNumber(data.last_activity_at_secs, 0),
            name,
        });
    });

    channel.on('do_not_disturb_changed', (data) => {
        store.dispatch('putAgentInDoNotDisturb', {
            state: data.state,
            status: data.status,
            name,
        });
    });

    channel.on('agent_status_change', (data) => {
        store.dispatch('changeAgentStatus', {
            agentStatus: data.agent_status,
            name,
        });
    });

    return channel;
}
